import React from "react";
import { useForm, type SubmitHandler } from "react-hook-form";
import styles from "./ChangePasswordModal.module.scss";
import Modal from "@/components/Modal/Modal";
import Button from "@/components/Button/Button";
import FormControl from "@/components/FormControl/FormControl";
import { requiredMessage } from "@/lib/utils";
import { useChangePassword } from "@/hooks/queries/useChangePassword";

interface ChangePasswordModalProps {
  isOpen: boolean;
  email: string;
  onClose: () => void;
}

interface ChangePasswordFormValues {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  email,
  onClose,
}) => {
  const changePassword = useChangePassword();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { isValid, errors },
  } = useForm<ChangePasswordFormValues>({
    defaultValues: { currentPassword: "", newPassword: "", confirmNewPassword: "" },
    mode: "onTouched",
  });

  const closeModal = () => {
    reset();
    onClose();
  };

  const onSubmit: SubmitHandler<ChangePasswordFormValues> = async (values) => {
    await changePassword.mutateAsync(
      {
        email,
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      },
      { onSuccess: closeModal }
    );
  };

  return (
    <Modal
      modalOpen={isOpen}
      closeModal={closeModal}
      heading="Change Password"
      className={styles.changePasswordModal}
    >
      <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        <FormControl
          name="currentPassword"
          register={register}
          controlType="password"
          label="Current Password"
          placeholder="Enter current password"
          validations={{ required: requiredMessage("Current Password") }}
          errorMessage={errors?.currentPassword?.message}
          isRequired
        />

        <FormControl
          name="newPassword"
          register={register}
          controlType="password"
          label="New Password"
          placeholder="Enter new password"
          validations={{
            required: requiredMessage("New Password"),
            minLength: { value: 8, message: "Password must be at least 8 characters" },
          }}
          errorMessage={errors?.newPassword?.message}
          isRequired
        />

        <FormControl
          name="confirmNewPassword"
          register={register}
          controlType="password"
          label="Confirm New Password"
          placeholder="Re-enter new password"
          validations={{
            required: requiredMessage("Confirm New Password"),
            validate: (value: string) =>
              value === watch("newPassword") || "Passwords do not match",
          }}
          errorMessage={errors?.confirmNewPassword?.message}
          isRequired
        />

        <div className={styles.actions}>
          <Button
            outlined
            type="button"
            className={styles.cancelButton}
            onClick={closeModal}
            disabled={changePassword.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className={styles.submitButton}
            disabled={!isValid || changePassword.isPending}
          >
            {changePassword.isPending ? "Updating..." : "Update Password"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ChangePasswordModal;
